import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Tag, message, Popconfirm, Tooltip, Row, Col } from 'antd';
import { PlusOutlined, DeleteOutlined, ReloadOutlined } from '@ant-design/icons';
import { useOutletContext } from 'react-router-dom';
import { USERDATA } from '../../App';
import { YouTubeOAuthToken } from './types';
import { listOAuthTokens, deleteOAuthToken, getOAuthAuthUrl, refreshTokenChannelInfo } from './api';
import { ComponentTitle } from '../custom-components';

const YouTubeLiveConnections: React.FC = () => {
  const { user } = useOutletContext<{ user: USERDATA }>();
  const [tokens, setTokens] = useState<YouTubeOAuthToken[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText] = useState('');
  const [refreshingTokens, setRefreshingTokens] = useState<Record<string, boolean>>({});

  const loadTokens = async () => {
    setLoading(true);
    try {
      const data = await listOAuthTokens();
      setTokens(data);
    } catch (err) {
      message.error('Failed to load OAuth connections');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTokens();
  }, []);

  const handleConnectNew = async () => {
    try {
      const authUrl = await getOAuthAuthUrl(user.id);
      window.open(authUrl, '_blank');
      message.info('Complete the YouTube authorization in the new window, then click Refresh below.');
    } catch (err) {
      message.error('Failed to get OAuth URL');
      console.error(err);
    }
  };

  const handleDelete = async (tokenId: string) => {
    try {
      await deleteOAuthToken(tokenId);
      message.success('Connection deleted');
      setTokens(prev => prev.filter(t => t.id !== tokenId));
    } catch (err) {
      message.error('Failed to delete connection');
      console.error(err);
    }
  };

  const handleRefreshToken = async (tokenId: string) => {
    setRefreshingTokens(prev => ({ ...prev, [tokenId]: true }));
    try {
      const { channelId, channelTitle } = await refreshTokenChannelInfo(tokenId);
      setTokens(prev =>
        prev.map(t =>
          t.id === tokenId
            ? {
                ...t,
                youtube_channel_id: channelId,
                youtube_channel_title: channelTitle || t.youtube_channel_title,
                token_status: 'active' as const,
                token_status_error: undefined,
              }
            : t
        )
      );
      message.success('Channel info refreshed');
    } catch (err: any) {
      const errorMsg = err?.response?.data?.error || err?.message || 'Token may be expired or revoked';
      setTokens(prev =>
        prev.map(t =>
          t.id === tokenId
            ? { ...t, token_status: 'error' as const, token_status_error: errorMsg }
            : t
        )
      );
      message.error(errorMsg);
      console.error(err);
    } finally {
      setRefreshingTokens(prev => ({ ...prev, [tokenId]: false }));
    }
  };

  const renderStatus = (record: YouTubeOAuthToken) => {
    const status = record.token_status || 'unknown';
    if (status === 'active') {
      return <Tag color="green">Active</Tag>;
    }
    if (status === 'error') {
      return (
        <Tooltip title={record.token_status_error}>
          <Tag color="red">Error</Tag>
        </Tooltip>
      );
    }
    return <Tag color="default">Unknown</Tag>;
  };

  const filteredTokens = tokens.filter(t => {
    if (!searchText) return true;
    const search = searchText.toLowerCase();
    return (
      t.youtube_channel_title?.toLowerCase().includes(search) ||
      t.youtube_channel_id?.toLowerCase().includes(search)
    );
  });

  const columns = [
    {
      title: 'Channel Title',
      dataIndex: 'youtube_channel_title',
      key: 'youtube_channel_title',
      render: (val: string) => val || '-',
    },
    {
      title: 'Channel ID',
      dataIndex: 'youtube_channel_id',
      key: 'youtube_channel_id',
      render: (val: string) => val || '-',
    },
    {
      title: 'Status',
      key: 'status',
      render: (_: any, record: YouTubeOAuthToken) => renderStatus(record),
    },
    {
      title: 'Created By',
      dataIndex: 'created_by',
      key: 'created_by',
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (val: string) => val ? new Date(val).toLocaleString() : '-',
    },
    {
      title: 'Last Used',
      dataIndex: 'last_used_at',
      key: 'last_used_at',
      render: (val: string) => val ? new Date(val).toLocaleString() : '-',
    },
    {
      title: 'Expires At',
      dataIndex: 'expires_at',
      key: 'expires_at',
      render: (val: string) => val ? new Date(val).toLocaleString() : '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: YouTubeOAuthToken) => (
        <Space>
          <Button
            type="text"
            icon={<ReloadOutlined spin={refreshingTokens[record.id]} />}
            onClick={() => handleRefreshToken(record.id)}
            loading={refreshingTokens[record.id]}
            title="Refresh channel info"
          />
          <Popconfirm
            title="Delete this connection?"
            description="This will remove the OAuth connection. Streams using it may stop working."
            onConfirm={() => handleDelete(record.id)}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Button type="text" danger icon={<DeleteOutlined />} title="Delete connection" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <ComponentTitle>YouTube Live Connections</ComponentTitle>
        </Col>
        <Col>
          <Space>
          <Button icon={<ReloadOutlined />} onClick={loadTokens}>Refresh</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleConnectNew}>
            Connect New Channel
          </Button>
        </Space>
        </Col>
      </Row>
      <Table
        columns={columns}
        dataSource={filteredTokens}
        rowKey="id"
        loading={loading}
        pagination={false}
        locale={{
          emptyText: (
            <div style={{ padding: 40 }}>
              <p>No OAuth connections found.</p>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleConnectNew}>
                Connect New Channel
              </Button>
            </div>
          ),
        }}
      />
    </div>
  );
};

export default YouTubeLiveConnections;
